import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Grid, BodyText } from './ui';
import CinematicHero from './ui/CinematicHero';
import { DesignSystem, getColor, getSpacing, getShadow } from '../styles/designSystem';
import { TourismAPI, BookingRequest, PricingResponse, API_BASE_URL } from '../api/tourism';
import AuthModal from './AuthModal';
import AppHeader from './Header';
import { useHeaderItineraries } from '../hooks/useHeaderItineraries';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Destination {
  id: number;
  title: string;
  state: string;
  duration: string;
}

interface BookingFormProps {
  destination: Destination | null;
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onNavigateTo?: (page: string) => void;
}

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const PageContainer = styled.div<{ $isOpen: boolean }>`
  background: #FAFAFA;
  color: ${getColor('primary.900')};
  font-family: ${DesignSystem.typography.fonts.primary};
  line-height: 1.6;
  min-height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  width: 100%;
  z-index: ${DesignSystem.zIndex.modal + 1};
  overflow-y: auto;
  overflow-x: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

const ContentSection = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 60px 48px 80px;
  animation: ${fadeInUp} 0.6s ease-out;
  
  @media (max-width: 768px) {
    padding: 40px 24px 60px;
  }
`;

// ─── Back Button (Hero top-left slot) ─────────────────────────────────────────

const HeroBackButton = styled.button`
  position: absolute;
  top: 100px;
  left: 24px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: transparent;
  color: white;
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 768px) {
    top: 80px;
    left: 16px;
    width: 40px;
    height: 40px;
    font-size: 1.1rem;
  }
`;

// ─── Section Headers (matching DestinationPage) ──────────────────────────────

const SectionLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #A59D84;
`;

const SectionTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: 400;
  color: ${getColor('primary.900')};
  margin: 0;
  font-family: ${DesignSystem.typography.fonts.display};
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const SectionTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 40px;
`;

// ─── Form Elements ────────────────────────────────────────────────────────────

const FormCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: ${DesignSystem.borderRadius.xl};
  border: 1px solid ${getColor('primary.200')};
  box-shadow: ${getShadow('subtle')};
  
  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 32px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #A59D84;
  margin-bottom: 12px;
  font-family: ${DesignSystem.typography.fonts.primary};
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const GroupSizeButton = styled.button<{ $isSelected: boolean }>`
  width: 48px;
  height: 48px;
  border: 1px solid ${props => props.$isSelected ? '#A59D84' : getColor('primary.200')};
  border-radius: ${DesignSystem.borderRadius.lg};
  background: ${props => props.$isSelected ? 'rgba(165, 157, 132, 0.08)' : 'white'};
  color: ${props => props.$isSelected ? '#A59D84' : getColor('primary.900')};
  font-weight: ${props => props.$isSelected ? 700 : 500};
  font-size: 0.95rem;
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${DesignSystem.typography.fonts.primary};
  
  &:hover {
    border-color: #A59D84;
    background: rgba(165, 157, 132, 0.05);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const OptionCard = styled.div<{ $isSelected: boolean }>`
  padding: 16px 20px;
  border: 1px solid ${props => props.$isSelected ? '#A59D84' : getColor('primary.200')};
  border-radius: ${DesignSystem.borderRadius.lg};
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  background: ${props => props.$isSelected ? 'rgba(165, 157, 132, 0.06)' : 'white'};
  will-change: auto;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  
  &:hover {
    border-color: #A59D84;
    transform: translateY(-2px);
    box-shadow: ${getShadow('soft')};
  }
`;

const OptionCardTitle = styled.div<{ $isSelected: boolean }>`
  font-weight: 600;
  font-size: 0.95rem;
  color: ${props => props.$isSelected ? '#A59D84' : getColor('primary.900')};
  margin-bottom: 2px;
  font-family: ${DesignSystem.typography.fonts.primary};
  transition: color ${DesignSystem.motion.duration.fast} ease;
`;

const OptionCardDescription = styled.div`
  font-size: 0.85rem;
  color: ${getColor('neutral.gray.500')};
  line-height: 1.5;
  font-family: ${DesignSystem.typography.fonts.primary};
`;

const DurationBadge = styled.div`
  padding: 14px 20px;
  background: rgba(165, 157, 132, 0.06);
  border-radius: ${DesignSystem.borderRadius.lg};
  border: 1px solid ${getColor('primary.200')};
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DurationLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${getColor('neutral.gray.500')};
`;

const DurationValue = styled.span`
  font-size: 1.1rem;
  font-weight: 400;
  color: ${getColor('primary.900')};
  font-family: ${DesignSystem.typography.fonts.display};
`;

// ─── Pricing ──────────────────────────────────────────────────────────────────

const PricingCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: ${DesignSystem.borderRadius.xl};
  text-align: center;
  border: 1px solid ${getColor('primary.200')};
  box-shadow: ${getShadow('subtle')};
  position: sticky;
  top: 100px;
  
  @media (max-width: 1024px) {
    position: relative;
    top: auto;
  }
  
  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const PricingTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 400;
  color: ${getColor('primary.900')};
  margin: 0 0 32px 0;
  font-family: ${DesignSystem.typography.fonts.display};
  letter-spacing: -0.02em;
`;

const PriceTotal = styled.div`
  font-size: 2.75rem;
  font-weight: 300;
  color: #A59D84;
  font-family: ${DesignSystem.typography.fonts.display};
  letter-spacing: -0.03em;
  margin-bottom: 8px;
  transition: opacity ${DesignSystem.motion.duration.fast} ease;
`;

const PriceSummary = styled.div`
  font-size: 0.85rem;
  color: ${getColor('neutral.gray.500')};
  margin-bottom: 32px;
  font-weight: 500;
`;

const PriceBreakdown = styled.div`
  text-align: left;
  padding: 24px;
  border-radius: ${DesignSystem.borderRadius.lg};
  border: 1px solid ${getColor('primary.200')};
  margin-bottom: 32px;
`;

const BreakdownHeader = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #A59D84;
  margin-bottom: 20px;
`;

const BreakdownRow = styled.div<{ $isTotal?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.$isTotal ? '16px 0 0 0' : '10px 0'};
  border-bottom: ${props => props.$isTotal ? 'none' : `1px solid ${getColor('primary.100')}`};
  border-top: ${props => props.$isTotal ? `1px solid ${getColor('primary.200')}` : 'none'};
  margin-top: ${props => props.$isTotal ? '8px' : '0'};
  
  &:last-child {
    border-bottom: none;
  }
`;

const BreakdownLabel = styled.span<{ $isTotal?: boolean }>`
  font-size: ${props => props.$isTotal ? '0.95rem' : '0.9rem'};
  font-weight: ${props => props.$isTotal ? 600 : 400};
  color: ${props => props.$isTotal ? getColor('primary.900') : getColor('primary.700')};
`;

const BreakdownValue = styled.span<{ $isTotal?: boolean }>`
  font-size: ${props => props.$isTotal ? '1.05rem' : '0.9rem'};
  font-weight: 600;
  color: ${props => props.$isTotal ? '#A59D84' : getColor('primary.900')};
`;

const CTAButton = styled.button`
  padding: 14px 32px;
  font-size: 0.75rem;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  background: #A59D84;
  border-radius: 50px;
  border: 1px solid #A59D84;
  color: white;
  cursor: pointer;
  box-shadow: none;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  
  &:hover {
    background: #9a8e7a;
    border-color: #9a8e7a;
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CalculatingState = styled.div`
  color: ${getColor('neutral.gray.400')};
  font-size: 1rem;
  font-family: ${DesignSystem.typography.fonts.primary};
  padding: 40px 0;
`;

const ActivityCheckmark = styled.span<{ $isSelected: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1.5px solid ${props => props.$isSelected ? '#A59D84' : getColor('primary.200')};
  background: ${props => props.$isSelected ? '#A59D84' : 'white'};
  color: white;
  font-size: 0.7rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
  transition: all ${DesignSystem.motion.duration.fast} ease;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const BookingForm: React.FC<BookingFormProps> = ({ destination, isOpen, onClose, onBack, onNavigateTo }) => {
  // Extract duration from destination prop (e.g., "4 days" -> 4)
  const durationFromDestination = destination?.duration 
    ? parseInt(destination.duration.split(' ')[0]) 
    : 5;
  
  const [formData, setFormData] = useState<BookingRequest>({
    duration_days: durationFromDestination,
    transport_type: 'car',
    package_category: 'standard' as 'standard' | 'deluxe' | 'premium',
    group_size: 2,
    selected_activities: [],
    state: 'ladakh'
  });
  
  const [pricing, setPricing] = useState<PricingResponse | null>(null);
  const [activities, setActivities] = useState<Array<{ activity_id: string; activity_name: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const pricingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update duration when destination changes
  useEffect(() => {
    if (destination?.duration) {
      const newDuration = parseInt(destination.duration.split(' ')[0]);
      setFormData(prev => ({ ...prev, duration_days: newDuration }));
    }
  }, [destination?.duration]);

  useEffect(() => {
    if (destination && isOpen) {
      fetchActivities();
      calculatePricingImmediate();
    }
  }, [destination, isOpen]);

  // Debounced pricing on form changes (500ms)
  useEffect(() => {
    if (!destination || !isOpen) return;
    
    if (pricingTimerRef.current) {
      clearTimeout(pricingTimerRef.current);
    }
    pricingTimerRef.current = setTimeout(() => {
      calculatePricingImmediate();
    }, 500);
    
    return () => {
      if (pricingTimerRef.current) {
        clearTimeout(pricingTimerRef.current);
      }
    };
  }, [formData]);

  const fetchActivities = async () => {
    if (!destination) return;
    
    try {
      const data = await TourismAPI.getAvailableActivities(destination.state);
      setActivities((data.activities || []).map(activity => ({
        activity_id: activity.activity_id,
        activity_name: activity.activity_name
      })));
    } catch (error) {
      console.error('Error fetching activities:', error);
      setActivities([]);
    }
  };

  const calculatePricingImmediate = async () => {
    if (!destination) return;
    
    setIsCalculating(true);
    try {
      const request: BookingRequest = {
        ...formData,
        state: destination.state.toLowerCase().replace(/\s+pradesh$/i, '')
      };
      
      const data = await TourismAPI.calculatePricing(request);
      setPricing(data);
    } catch (error) {
      console.error('Error calculating pricing:', error);
      setPricing(null);
    } finally {
      setIsCalculating(false);
    }
  };

  const handleInputChange = useCallback((field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const { destinationItineraries, trekkingItineraries, bikeItineraries } = useHeaderItineraries();

  const handleActivityToggle = useCallback((activity: string) => {
    setFormData(prev => ({
      ...prev,
      selected_activities: prev.selected_activities.includes(activity)
        ? prev.selected_activities.filter(a => a !== activity)
        : [...prev.selected_activities, activity]
    }));
  }, []);

  const handleBookingSubmit = async () => {
    // Open authentication modal to get user details
    setIsAuthModalOpen(true);
  };

  const handleAuthSuccess = async (userData: { name: string; email: string; phone: string }) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const booking = await TourismAPI.createBooking({
        ...formData,
        state: destination?.state.toLowerCase().replace(/\s+pradesh$/i, '') || 'ladakh',
        customer_name: userData.name,
        customer_email: userData.email,
        customer_phone: userData.phone
      });

      setIsAuthModalOpen(false);
      alert(`Thank you, ${userData.name}! Your booking ${booking.booking_id} has been received. We'll contact you soon.`);
      onClose();
    } catch (error) {
      console.error('Error creating booking:', error);
      alert(error instanceof Error ? error.message : 'Unable to create your booking. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!destination) return null;

  return (
    <PageContainer $isOpen={isOpen}>
      <AppHeader
        onNavigateTo={onNavigateTo}
        currentPage="booking"
        transparent
        destinationItineraries={destinationItineraries}
        trekkingItineraries={trekkingItineraries}
        bikeItineraries={bikeItineraries}
      />

      {/* Cinematic Hero — matching site-wide pattern */}
      <CinematicHero
        bgImage={`${API_BASE_URL}/api/images/booking_form/header_humans.jpg`}
        height="100vh"
        mobileHeight="100vh"
        brightness={1.1}
        contrast={1.1}
        title={<>Complete Your <span>Journey</span></>}
        subtitle="Customize every detail of your adventure. Choose your transport, package, group size, and add optional activities."
        topLeftSlot={
          <HeroBackButton onClick={onBack} title="Go back to destination">
            ←
          </HeroBackButton>
        }
      />

      {/* Form Content */}
      <ContentSection>
        <SectionTitleGroup>
          <SectionLabel>Customize Your Trip</SectionLabel>
          <SectionTitle>Trip Details</SectionTitle>
        </SectionTitleGroup>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '48px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px' }}>
            {/* Left Column — Form */}
            <div style={{ minWidth: 0 }}>
              <FormCard>
                {/* Duration Badge */}
                <DurationBadge>
                  <DurationLabel>Duration</DurationLabel>
                  <DurationValue>{formData.duration_days} Days</DurationValue>
                </DurationBadge>

                <FormGroup>
                  <Label>Group Size</Label>
                  <ButtonGroup>
                    {[1, 2, 4, 5, 6, 7, 8].map((size) => (
                      <GroupSizeButton
                        key={size}
                        $isSelected={formData.group_size === size}
                        onClick={() => handleInputChange('group_size', size)}
                      >
                        {size}
                      </GroupSizeButton>
                    ))}
                  </ButtonGroup>
                </FormGroup>

                <FormGroup>
                  <Label>Transport Type</Label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <OptionCard 
                      $isSelected={formData.transport_type === 'car'}
                      onClick={() => handleInputChange('transport_type', 'car')}
                    >
                      <OptionCardTitle $isSelected={formData.transport_type === 'car'}>Car Only</OptionCardTitle>
                      <OptionCardDescription>Comfortable 4-wheeler transport throughout your journey</OptionCardDescription>
                    </OptionCard>
                    <OptionCard 
                      $isSelected={formData.transport_type === 'car_bike'}
                      onClick={() => handleInputChange('transport_type', 'car_bike')}
                    >
                      <OptionCardTitle $isSelected={formData.transport_type === 'car_bike'}>Car + Bike</OptionCardTitle>
                      <OptionCardDescription>Car transport with motorcycle rental for adventure rides</OptionCardDescription>
                    </OptionCard>
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Package Category</Label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[
                      { value: 'standard', label: 'Standard', desc: 'Essential comfort with authentic local experiences' },
                      { value: 'deluxe', label: 'Deluxe', desc: 'Enhanced comfort with premium stays and activities' },
                      { value: 'premium', label: 'Premium', desc: 'Luxury experiences with top-tier accommodations' }
                    ].map(pkg => (
                      <OptionCard 
                        key={pkg.value}
                        $isSelected={formData.package_category === pkg.value}
                        onClick={() => handleInputChange('package_category', pkg.value)}
                      >
                        <OptionCardTitle $isSelected={formData.package_category === pkg.value}>{pkg.label}</OptionCardTitle>
                        <OptionCardDescription>{pkg.desc}</OptionCardDescription>
                      </OptionCard>
                    ))}
                  </div>
                </FormGroup>

                {activities.length > 0 && (
                  <FormGroup>
                    <Label>Additional Activities</Label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {activities.map((activity) => (
                        <OptionCard 
                          key={activity.activity_id}
                          $isSelected={formData.selected_activities.includes(activity.activity_id)}
                          onClick={() => handleActivityToggle(activity.activity_id)}
                          style={{ display: 'flex', alignItems: 'center' }}
                        >
                          <ActivityCheckmark $isSelected={formData.selected_activities.includes(activity.activity_id)}>
                            {formData.selected_activities.includes(activity.activity_id) ? '✓' : ''}
                          </ActivityCheckmark>
                          <span style={{ fontWeight: 500, fontSize: '0.9rem', color: getColor('primary.900') }}>
                            {activity.activity_name}
                          </span>
                        </OptionCard>
                      ))}
                    </div>
                  </FormGroup>
                )}
              </FormCard>
            </div>

            {/* Right Column — Pricing */}
            <div style={{ minWidth: 0 }}>
              <PricingCard>
                <PricingTitle>
                  {isCalculating ? 'Calculating...' : 'Your Journey Estimate'}
                </PricingTitle>
                
                {pricing && !isCalculating ? (
                  <>
                    <PriceTotal>
                      ₹{(pricing.total_price ?? 0).toLocaleString()}
                    </PriceTotal>
                    <PriceSummary>
                      {pricing.group_size} {pricing.group_size === 1 ? 'person' : 'people'} · {pricing.duration_days} days
                    </PriceSummary>
                    
                    {pricing.price_breakdown && (
                    <PriceBreakdown>
                      <BreakdownHeader>Price Breakdown</BreakdownHeader>
                      <BreakdownRow>
                        <BreakdownLabel>Base Package</BreakdownLabel>
                        <BreakdownValue>₹{(pricing.price_breakdown.base_package ?? 0).toLocaleString()}</BreakdownValue>
                      </BreakdownRow>
                      <BreakdownRow>
                        <BreakdownLabel>Transport & Fuel</BreakdownLabel>
                        <BreakdownValue>₹{((pricing.price_breakdown.transport_daily ?? 0) + (pricing.price_breakdown.fuel_charges ?? 0)).toLocaleString()}</BreakdownValue>
                      </BreakdownRow>
                      {(pricing.price_breakdown.bike_rental ?? 0) > 0 && (
                        <BreakdownRow>
                          <BreakdownLabel>Bike Rental</BreakdownLabel>
                          <BreakdownValue>₹{pricing.price_breakdown.bike_rental.toLocaleString()}</BreakdownValue>
                        </BreakdownRow>
                      )}
                      {(pricing.price_breakdown.activities ?? 0) > 0 && (
                        <BreakdownRow>
                          <BreakdownLabel>Activities</BreakdownLabel>
                          <BreakdownValue>₹{pricing.price_breakdown.activities.toLocaleString()}</BreakdownValue>
                        </BreakdownRow>
                      )}
                      <BreakdownRow $isTotal>
                        <BreakdownLabel $isTotal>Taxes & Fees</BreakdownLabel>
                        <BreakdownValue $isTotal>₹{(pricing.price_breakdown.taxes_fees ?? 0).toLocaleString()}</BreakdownValue>
                      </BreakdownRow>
                    </PriceBreakdown>
                    )}

                    <CTAButton onClick={handleBookingSubmit} disabled={isLoading}>
                      {isLoading ? 'Submitting...' : 'Confirm & Contact Us'}
                    </CTAButton>
                  </>
                ) : (
                  <CalculatingState>
                    Calculating your perfect journey...
                  </CalculatingState>
                )}
              </PricingCard>
            </div>
          </div>
        </div>
      </ContentSection>

      {/* Authentication Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onAuthSuccess={handleAuthSuccess}
      />
    </PageContainer>
  );
};

export default BookingForm;